import React, { useEffect, useState } from 'react';
import type { WUploadedImageView } from '@zola/svc-web-api-ts-client';

import UploadcareWidget from 'components/common/UploadcareWidget/UploadcareWidget';
import { ImageUploadStateProps, ImageUploadDispatchProps } from 'components/common/ImageUpload';

type ImageUploadProps = ImageUploadStateProps &
  ImageUploadDispatchProps & {
    imageElement: WUploadedImageView | null;
    onUploadComplete?: (zolaFile: WUploadedImageView) => void;
    renderButton: (busy: boolean, editable: boolean, onClick: () => void) => JSX.Element;
    imageSizeRequirements?: {
      minWidth: number;
      minHeight: number;
      minCrop?: {
        width: number;
        height: number;
      };
    };
    resetAfterSubmit?: boolean;
    allowedFileTypes?: string[];
  };

const ImageUpload: React.FC<ImageUploadProps> = ({
  copyImageToZola,
  imageElement,
  imageSizeRequirements = {
    minWidth: 1,
    minHeight: 1,
    minCrop: {
      width: 1,
      height: 1,
    },
  },
  loadCache,
  onUploadComplete,
  renderButton,
  cachedMetadata,
  zolaFileUploaded,
  resetAfterSubmit,
  allowedFileTypes,
}) => {
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [cachedUrl, setCachedUrl] = useState<string | null>(null);
  const { minCrop } = imageSizeRequirements;

  useEffect(() => {
    // use cached image, unless explicitly told to not cache image
    if (!resetAfterSubmit) {
      const zolaImageId = imageElement?.uuid || '';
      const metadata = cachedMetadata?.[zolaImageId];
      const url = metadata?.uploadcare_url || null;
      // If the Zola image is set but its cache hasn't been loaded yet, fetch it.
      if (zolaImageId && !metadata) {
        loadCache(zolaImageId);
      }

      // To avoid useless rerenders, only update cachedUrl when it actually changes.
      if (cachedUrl !== url) {
        setCachedUrl(url);
      }
    }
  }, [cachedMetadata, imageElement, resetAfterSubmit, cachedUrl, loadCache]);

  const saveUploadcareImage = (
    uploadcareFile: WUploadedImageView
  ): Promise<WUploadedImageView> | void => {
    setIsBusy(true);

    copyImageToZola(uploadcareFile, cachedMetadata)
      .then(zolaFile => {
        setIsBusy(false);
        zolaFileUploaded(zolaFile);
        if (onUploadComplete) onUploadComplete(zolaFile);
      })
      .catch(() => {
        setIsBusy(false);
      });
  };

  const cropRatio = minCrop
    ? `${minCrop.width}:${minCrop.height} minimum`
    : `${imageSizeRequirements.minWidth}:${imageSizeRequirements.minHeight} minimum`;

  return (
    <UploadcareWidget
      cachedValue={cachedUrl}
      cropRatio={cropRatio}
      minWidth={imageSizeRequirements.minWidth}
      minHeight={imageSizeRequirements.minHeight}
      onUploadcareFileReady={saveUploadcareImage}
      isBusy={isBusy}
      resetAfterSubmit={resetAfterSubmit}
      renderButton={renderButton}
      allowedFileTypes={allowedFileTypes}
    />
  );
};

export default ImageUpload;



// WEBPACK FOOTER //
// ./src/components/common/ImageUpload/ImageUpload.tsx